import { inject, Injectable, DOCUMENT } from '@angular/core';
import { UnleashService } from '@karelics/angular-unleash-proxy-client';
import { Observable, timer } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { STAR_NOTICE_BANNER_UNLEASH_KEY } from '../../tokens/unleash-flags.token';

export const STAR_NOTICE_MODAL_DIALOG_ID = 'star-notice-modal';
export const STAR_NOTICE_MODAL_STORAGE_KEY = 'star_notice_modal_acknowledged';

// `DialogService` mounts dialogs into this container, which only exists once
// `rd-core-main-container` has rendered. Router navigation starts as soon as the
// auth state resolves, which can be before that, so callers have to wait for it.
const DIALOG_CONTAINER_SELECTOR = '.rd-dialog-container';
const DIALOG_CONTAINER_POLL_INTERVAL_MS = 50;
// 200 * 50ms — gives up after roughly ten seconds.
const DIALOG_CONTAINER_POLL_ATTEMPTS = 200;

@Injectable({ providedIn: 'root' })
export class StarNoticeModalService {
  private readonly document = inject(DOCUMENT);
  private readonly unleashService = inject(UnleashService);

  // Shares the notice banner's flag: the two are one temporary announcement and
  // have to be switched on and off together.
  get isEnabled(): boolean {
    return this.unleashService.isEnabled(STAR_NOTICE_BANNER_UNLEASH_KEY);
  }

  /**
   * The acknowledgement is per device rather than per member, so a member who
   * logs in again on the same browser is not shown the notice a second time.
   */
  get isAcknowledged(): boolean {
    return this.storage?.getItem(STAR_NOTICE_MODAL_STORAGE_KEY) === 'true';
  }

  acknowledge(): void {
    this.storage?.setItem(STAR_NOTICE_MODAL_STORAGE_KEY, 'true');
  }

  /**
   * Emits once the dialog host is in the DOM, or completes without emitting if
   * it never turns up — a missing notice is preferable to a wedged navigation.
   */
  whenDialogHostReady(): Observable<number> {
    return timer(0, DIALOG_CONTAINER_POLL_INTERVAL_MS).pipe(
      take(DIALOG_CONTAINER_POLL_ATTEMPTS),
      filter(() =>
        Boolean(this.document.querySelector(DIALOG_CONTAINER_SELECTOR)),
      ),
      take(1),
    );
  }

  // Private browsing modes can throw on access, and the guard must not break
  // navigation when that happens.
  private get storage(): Storage | null {
    try {
      return this.document.defaultView?.localStorage ?? null;
    } catch {
      return null;
    }
  }
}
